/* eslint-disable i18next/no-literal-string */
import {endpoints} from './endpoints'

const VALIDATION_ERROR = 'VALIDATION_ERROR'

interface FrontendErrorPayload {
    url: string
    status: number
    code: string
    message: string
    userAgent: string
    sourceType?: string
    pageUrl?: string
    stackTrace?: string
    errorDetail?: string
    requestHeaders?: Record<string, string>
    responseHeaders?: Record<string, string>
}

const FRONTEND_ERROR_ENDPOINT = endpoints.admin.frontendError()

// WHY (issue 1078): when the current session lacks permission to POST the
// frontend-error endpoint, Spring returns 403 + logs an AuthorizationDenied
// row to `error_logs` on every attempt. During a 500 storm that turns into
// dozens of identical rows per polling cycle. Latch the breaker after the
// first 403 and skip subsequent POSTs for the rest of the page lifetime.
let frontendErrorBreakerTripped = false

export function __resetErrorReporterCircuitBreakerForTests(): void {
    frontendErrorBreakerTripped = false
}

/**
 * Reports a frontend error to the backend. Fire-and-forget — never blocks the UI.
 * Skips 401 errors (expected session expiry) and errors from the error endpoint itself (infinite loop prevention).
 */
export function reportApiError(requestUrl: string, status: number, code: string, message: string,
                               errorDetail?: string,
                               requestHeaders?: Record<string, string>,
                               responseHeaders?: Record<string, string>,
                               queryKey?: string): Promise<string | null> {
    if (status === 401) return Promise.resolve(null)
    if (status === 400 && code === VALIDATION_ERROR) return Promise.resolve(null)
    if (requestUrl.includes(FRONTEND_ERROR_ENDPOINT)) return Promise.resolve(null)

    return sendErrorReport({
        url: requestUrl,
        status,
        code,
        message,
        userAgent: navigator.userAgent,
        pageUrl: window.location.href,
        stackTrace: JSON.stringify({
            queryKey: queryKey ?? null,
            request: {url: requestUrl, status, headers: requestHeaders ?? {}},
            response: {body: errorDetail ?? '', headers: responseHeaders ?? {}},
        }),
        errorDetail,
        requestHeaders,
        responseHeaders,
    })
}

/**
 * Reports an unhandled JavaScript error to the backend.
 */
export function reportJavaScriptError(message: string, source?: string, stack?: string): Promise<string | null> {
    return sendErrorReport({
        url: source ?? window.location.href,
        status: 0,
        code: 'JAVASCRIPT_ERROR',
        message,
        userAgent: navigator.userAgent,
        sourceType: 'JavaScriptError',
        pageUrl: window.location.href,
        stackTrace: stack ?? new Error().stack,
    })
}

/**
 * Generates sample frontend errors for testing the error logging pipeline.
 */
export function generateSampleFrontendErrors(): void {
    const page = window.location.href
    const ua = navigator.userAgent
    const sampleReqHeaders = {'Content-Type': 'application/json', 'Accept-Language': 'en'}
    const sampleResHeaders = {'content-type': 'application/json', 'x-request-id': 'sample-abc-123'}

    const apiStackTrace = (url: string, status: number, queryKey: string) => JSON.stringify({
        queryKey,
        request: {url, status, headers: sampleReqHeaders},
        response: {
            body: `{"errors":[{"code":"GN0004","status":"${status}","detail":"Sample error"}]}`,
            headers: sampleResHeaders,
        },
    })

    // 3x same API error → should group as 1 template with 3 occurrences
    const samplePartyUrl = endpoints.parties.detail(999)
    for (let i = 0; i < 3; i++) {
        sendErrorReport({
            url: samplePartyUrl, status: 404, code: 'GN0002',
            message: 'Sample: Entity not found',
            userAgent: ua, pageUrl: page,
            stackTrace: apiStackTrace(samplePartyUrl, 404, '["parties","999"]'),
            errorDetail: '{"errors":[{"code":"GN0002","status":"404","detail":"Entity not found"}]}',
            requestHeaders: sampleReqHeaders, responseHeaders: sampleResHeaders,
        })
    }

    // 2x same JS error → should group
    for (let i = 0; i < 2; i++) {
        sendErrorReport({
            url: page, status: 0, code: 'JAVASCRIPT_ERROR',
            message: 'Sample: Cannot read properties of undefined (reading "name")',
            userAgent: ua, sourceType: 'JavaScriptError', pageUrl: page,
            stackTrace: 'TypeError: Cannot read properties of undefined (reading "name")\n'
                + '    at PartyDetail (PartyDetail.tsx:42:18)\n'
                + '    at renderWithHooks (react-dom.js:1234:22)',
        })
    }

    // 1x unique promise rejection
    sendErrorReport({
        url: page, status: 0, code: 'JAVASCRIPT_ERROR',
        message: 'Sample: Network request failed',
        userAgent: ua, sourceType: 'PromiseRejection', pageUrl: page,
        stackTrace: 'Error: Network request failed\n'
            + '    at fetch (client.ts:64:5)\n'
            + '    at api.get (client.ts:70:12)',
    })

    // 2x same 500 error → should group
    const sampleIdentityBulkUrl = endpoints.identities.bulk()
    for (let i = 0; i < 2; i++) {
        sendErrorReport({
            url: sampleIdentityBulkUrl, status: 500, code: 'GN0004',
            message: 'Sample: Internal server error',
            userAgent: ua, pageUrl: page,
            stackTrace: apiStackTrace(sampleIdentityBulkUrl, 500, '["identities","bulk"]'),
            errorDetail: '{"errors":[{"code":"GN0004","status":"500","detail":"Unexpected error"}]}',
            requestHeaders: sampleReqHeaders, responseHeaders: sampleResHeaders,
        })
    }
}

async function sendErrorReport(payload: FrontendErrorPayload): Promise<string | null> {
    if (frontendErrorBreakerTripped) return null
    try {
        const response = await fetch(FRONTEND_ERROR_ENDPOINT, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(payload),
        })
        if (response.ok) {
            const body = await response.json().catch(() => null)
            return body?.errorLogId ?? null
        }
        if (response.status === 403) {
            frontendErrorBreakerTripped = true
        }
    // eslint-disable-next-line no-restricted-syntax -- throwing from error reporting causes infinite loops
    } catch { /* intentionally empty — see above */ }
    return null
}
